<div class="cd-page">
  <app-counselor-nav></app-counselor-nav>

  <main class="cd-main" [attr.aria-label]="isEdit ? 'Edit career' : 'New career'">
    <!-- HEADER -->
    <div class="cd-header-row">
      <div>
        <h1 class="cd-title">{{ isEdit ? 'Edit career' : 'New career' }}</h1>
        <p class="cd-subtitle">
          {{
            isEdit
              ? 'Update this career page for your students'
              : 'Build a new career page for students to explore'
          }}
        </p>
      </div>
      <div class="header-actions">
        <button
          mat-button
          class="cd-back-btn"
          routerLink="/counselors/careers"
          aria-label="Back to career pages"
        >
          <mat-icon aria-hidden="true">arrow_back</mat-icon>
          Back
        </button>
        <button
          mat-stroked-button
          class="cd-draft-btn"
          (click)="save('Draft')"
          aria-label="Save as draft"
        >
          Save draft
        </button>
        <button
          mat-flat-button
          class="cd-publish-btn"
          (click)="save('Published')"
          aria-label="Publish career page"
        >
          Publish
        </button>
      </div>
    </div>

    <form [formGroup]="form" aria-label="Career page form">
      <div class="ce-layout">
        <!-- LEFT: MAIN FORM -->
        <div class="ce-main">
          <!-- BASICS -->
          <mat-card class="ce-card" appearance="outlined">
            <mat-card-content>
              <h2 class="ce-section-title">Basic info</h2>

              <mat-form-field appearance="fill" class="ce-field" subscriptSizing="dynamic">
                <mat-label>Career title</mat-label>
                <input matInput formControlName="name" aria-label="Career title" />
                <mat-error>Career title is required</mat-error>
              </mat-form-field>

              <mat-form-field appearance="fill" class="ce-field" subscriptSizing="dynamic">
                <mat-label>Tagline</mat-label>
                <input matInput formControlName="tagline" aria-label="Tagline" />
                <mat-error>Tagline is required</mat-error>
              </mat-form-field>

              <mat-form-field appearance="fill" class="ce-field" subscriptSizing="dynamic">
                <mat-label>Category</mat-label>
                <mat-select formControlName="career_type_id" aria-label="Select category">
                  <mat-option value="1">Select a category</mat-option>
                  @for (cat of categories; track cat.id) {
                    <mat-option [value]="cat.id">{{ cat.name }}</mat-option>
                  }
                </mat-select>
                @if (form.get('career_type_id')?.hasError('required')) {
                  <mat-error> Category is required </mat-error>
                }
              </mat-form-field>

              <mat-form-field
                appearance="fill"
                class="ce-field ce-emoji-field"
                subscriptSizing="dynamic"
              >
                <mat-label>Icon</mat-label>
                <mat-select formControlName="icon" aria-label="Select icon">
                  @for (option of emojiOptions; track option.emoji) {
                    <mat-option [value]="option.emoji">
                      {{ option.emoji }} {{ option.label }}
                    </mat-option>
                  }
                </mat-select>
                @if (form.get('icon')?.hasError('required')) {
                  <mat-error> Icon is required </mat-error>
                }
              </mat-form-field>

              <mat-form-field appearance="fill" class="ce-field" subscriptSizing="dynamic">
                <mat-label>Description</mat-label>
                <textarea
                  matInput
                  formControlName="description"
                  rows="5"
                  aria-label="Career description"
                  style="resize: vertical"
                  required
                ></textarea>
                <mat-error>Description is required</mat-error>
              </mat-form-field>
            </mat-card-content>
          </mat-card>

          <!-- BANNER PHOTO -->
          <mat-card class="ce-card" appearance="outlined">
            <mat-card-content>
              <h2 class="ce-section-title">Banner photo</h2>
              <p class="cd-subtitle" style="font-size: 12px; margin-bottom: 8px">
                Displays at the top of the career page. Use the slider to adjust vertical position.
              </p>

              <input
                #bannerInput
                type="file"
                accept="image/*"
                style="display: none"
                aria-label="Upload banner photo"
                (change)="onBannerSelected($event)"
              />

              @if (!bannerPreview) {
                <div
                  class="ce-upload-dropzone"
                  role="button"
                  tabindex="0"
                  aria-label="Click to upload banner photo"
                  (click)="bannerInput.click()"
                  (keydown.enter)="bannerInput.click()"
                  (keydown.space)="bannerInput.click()"
                >
                  <mat-icon class="ce-upload-icon" aria-hidden="true">add_photo_alternate</mat-icon>
                  <div class="ce-upload-label">Click to upload banner photo</div>
                  <div class="ce-upload-hint">JPG, PNG, WEBP — recommended 1200×400px or wider</div>
                </div>
              }

              @if (bannerPreview) {
                <div class="ce-banner-preview-wrap">
                  <div
                    class="ce-banner-preview"
                    role="img"
                    aria-label="Banner photo preview"
                    [style.backgroundImage]="'url(' + bannerPreview + ')'"
                    [style.backgroundPositionY]="bannerPositionY + '%'"
                  >
                    <div class="ce-banner-overlay" aria-hidden="true"></div>
                    <div class="ce-banner-preview-content" aria-hidden="true">
                      <div class="ce-banner-label-pill">Category</div>
                      <div class="ce-banner-title">Career Title</div>
                      <div class="ce-banner-tagline">Tagline text appears here</div>
                    </div>
                  </div>
                  <div class="ce-banner-slider-row">
                    <mat-icon style="font-size: 16px; color: #7a95a8" aria-hidden="true"
                      >arrow_upward</mat-icon
                    >
                    <mat-slider
                      min="0"
                      max="100"
                      step="1"
                      class="ce-banner-slider"
                      aria-label="Adjust banner vertical position"
                    >
                      <input
                        matSliderThumb
                        [(ngModel)]="bannerPositionY"
                        [ngModelOptions]="{ standalone: true }"
                        [attr.aria-valuenow]="bannerPositionY"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        [attr.aria-label]="'Banner position ' + bannerPositionY + '%'"
                      />
                    </mat-slider>
                    <mat-icon style="font-size: 16px; color: #7a95a8" aria-hidden="true"
                      >arrow_downward</mat-icon
                    >
                    <span class="ce-banner-pos-label" aria-live="polite" aria-atomic="true"
                      >{{ bannerPositionY }}%</span
                    >
                  </div>
                  <div style="display: flex; gap: 8px">
                    <button
                      mat-stroked-button
                      class="cd-draft-btn"
                      type="button"
                      aria-label="Replace banner photo"
                      (click)="bannerInput.click()"
                    >
                      Replace photo
                    </button>
                    <button
                      mat-button
                      class="cd-back-btn"
                      type="button"
                      aria-label="Remove banner photo"
                      (click)="removeBanner()"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              }
            </mat-card-content>
          </mat-card>

          <!-- STATS -->
          <mat-card class="ce-card" appearance="outlined">
            <mat-card-content>
              <h2 class="ce-section-title">Career stats</h2>
              <div class="ce-stats-grid">
                <mat-form-field appearance="fill" subscriptSizing="dynamic">
                  <mat-label>Avg. salary</mat-label>
                  <input matInput formControlName="salary" aria-label="Average salary" />
                </mat-form-field>
                <mat-form-field appearance="fill" subscriptSizing="dynamic">
                  <mat-label>Education required</mat-label>
                  <input
                    matInput
                    formControlName="required_education"
                    aria-label="Education required"
                  />
                </mat-form-field>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- SKILLS -->
          <mat-card class="ce-card" appearance="outlined">
            <mat-card-content>
              <h2 class="ce-section-title">Skills & keywords</h2>
              <div class="ce-tag-input-row">
                <mat-form-field appearance="fill" class="ce-tag-field" subscriptSizing="dynamic">
                  <mat-label>Add a skill</mat-label>
                  <input
                    matInput
                    formControlName="skills"
                    aria-label="Add a skill"
                    (keydown.enter)="addSkill()"
                  />
                </mat-form-field>
                <button
                  mat-flat-button
                  class="cd-publish-btn ce-add-btn"
                  type="button"
                  aria-label="Add skill"
                  (click)="addSkill()"
                >
                  Add
                </button>
              </div>
              <mat-chip-set class="ce-chips" aria-label="Added skills">
                @for (skill of skills; track skill.id) {
                  <mat-chip (removed)="removeSkill(skill)">
                    {{ skill.value }}
                    <button
                      matChipRemove
                      type="button"
                      [attr.aria-label]="'Remove skill ' + skill.value"
                    >
                      <mat-icon aria-hidden="true">cancel</mat-icon>
                    </button>
                  </mat-chip>
                }
              </mat-chip-set>
            </mat-card-content>
          </mat-card>

          <!-- GALLERY -->
          <mat-card class="ce-card" appearance="outlined">
            <mat-card-content>
              <div class="ce-section-header">
                <h2 class="ce-section-title">Photo gallery</h2>
                <button
                  mat-button
                  class="cd-back-btn"
                  type="button"
                  aria-label="Add photos to gallery"
                  (click)="galleryInput.click()"
                >
                  <mat-icon aria-hidden="true">add</mat-icon> Add photos
                </button>
              </div>
              <p class="cd-subtitle" style="font-size: 12px">
                These photos appear in the gallery section of the career page.
              </p>

              <input
                #galleryInput
                type="file"
                accept="image/*"
                multiple
                style="display: none"
                aria-label="Upload gallery photos"
                (change)="onGallerySelected($event)"
              />

              @if (galleryPhotos.length === 0) {
                <div
                  class="ce-upload-dropzone"
                  role="button"
                  tabindex="0"
                  aria-label="Click to upload gallery photos"
                  (click)="galleryInput.click()"
                  (keydown.enter)="galleryInput.click()"
                  (keydown.space)="galleryInput.click()"
                >
                  <mat-icon class="ce-upload-icon" aria-hidden="true">photo_library</mat-icon>
                  <div class="ce-upload-label">Click to upload photos</div>
                  <div class="ce-upload-hint">Select multiple images at once</div>
                </div>
              }

              @if (galleryPhotos.length > 0) {
                <div class="ce-gallery-grid" role="list" aria-label="Gallery photos">
                  @for (photo of galleryPhotos; track photo.id; let i = $index) {
                    <div class="ce-gallery-item" role="listitem">
                      <div
                        class="ce-gallery-thumb"
                        role="img"
                        [attr.aria-label]="galleryPhotos[i].caption || 'Gallery photo ' + (i + 1)"
                        [style.backgroundImage]="'url(' + photo.preview + ')'"
                      >
                        <button
                          mat-icon-button
                          class="ce-gallery-remove"
                          type="button"
                          (click)="removeGalleryPhoto(photo.id)"
                          [attr.aria-label]="'Remove gallery photo ' + (i + 1)"
                          matTooltip="Remove"
                        >
                          <mat-icon aria-hidden="true">close</mat-icon>
                        </button>
                      </div>
                      <mat-form-field
                        appearance="fill"
                        subscriptSizing="dynamic"
                        style="width: 100%"
                      >
                        <mat-label>Caption (optional)</mat-label>
                        <input
                          matInput
                          [(ngModel)]="galleryPhotos[i].caption"
                          [ngModelOptions]="{ standalone: true }"
                          [attr.aria-label]="'Caption for gallery photo ' + (i + 1)"
                        />
                      </mat-form-field>
                    </div>
                  }
                  <div
                    class="ce-gallery-add-tile"
                    role="button"
                    tabindex="0"
                    aria-label="Add more photos"
                    (click)="galleryInput.click()"
                    (keydown.enter)="galleryInput.click()"
                    (keydown.space)="galleryInput.click()"
                  >
                    <mat-icon style="color: #c5d0db; font-size: 28px" aria-hidden="true"
                      >add_photo_alternate</mat-icon
                    >
                    <span>Add more</span>
                  </div>
                </div>
              }
            </mat-card-content>
          </mat-card>

          <!-- JOB LISTINGS -->
          <mat-card class="ce-card" appearance="outlined">
            <mat-card-content>
              <div class="ce-section-header">
                <h2 class="ce-section-title">Job listings</h2>
                <button
                  mat-button
                  class="cd-back-btn"
                  type="button"
                  aria-label="Add job listing"
                  (click)="addJob()"
                >
                  <mat-icon aria-hidden="true">add</mat-icon> Add listing
                </button>
              </div>

              @for (job of jobListings; track job.id) {
                <div
                  class="ce-job-item"
                  role="group"
                  [attr.aria-label]="
                    (job.title || 'New listing') + (job.company ? ' at ' + job.company : '')
                  "
                >
                  <div class="ce-job-fields">
                    <div class="ce-job-row">
                      <mat-form-field appearance="fill" subscriptSizing="dynamic">
                        <mat-label>Job title</mat-label>
                        <input
                          matInput
                          [(ngModel)]="job.title"
                          [ngModelOptions]="{ standalone: true }"
                          aria-label="Job title"
                          (ngModelChange)="syncJobListings()"
                        />
                      </mat-form-field>
                      <mat-form-field appearance="fill" subscriptSizing="dynamic">
                        <mat-label>Company</mat-label>
                        <input
                          matInput
                          [(ngModel)]="job.company"
                          [ngModelOptions]="{ standalone: true }"
                          aria-label="Company name"
                          (ngModelChange)="syncJobListings()"
                        />
                      </mat-form-field>
                    </div>
                    <div class="ce-job-row">
                      <mat-form-field appearance="fill" subscriptSizing="dynamic">
                        <mat-label>Job Description</mat-label>
                        <textarea
                          matInput
                          [(ngModel)]="job.description"
                          [ngModelOptions]="{ standalone: true }"
                          aria-label="Job description"
                          (ngModelChange)="syncJobListings()"
                        ></textarea>
                      </mat-form-field>
                    </div>
                    <div class="ce-job-row">
                      <mat-form-field appearance="fill" subscriptSizing="dynamic">
                        <mat-label>Type</mat-label>
                        <mat-select
                          [(ngModel)]="job.type"
                          [ngModelOptions]="{ standalone: true }"
                          aria-label="Job type"
                          (ngModelChange)="syncJobListings()"
                        >
                          <mat-option value="Internship">Internship</mat-option>
                          <mat-option value="Full-time">Full-time</mat-option>
                          <mat-option value="Part-time">Part-time</mat-option>
                        </mat-select>
                      </mat-form-field>
                      <mat-form-field appearance="fill" subscriptSizing="dynamic">
                        <mat-label>Work Style</mat-label>
                        <mat-select
                          [(ngModel)]="job.work_style"
                          [ngModelOptions]="{ standalone: true }"
                          aria-label="Work style"
                          (ngModelChange)="syncJobListings()"
                        >
                          <mat-option value="Remote">Remote</mat-option>
                          <mat-option value="Hybrid">Hybrid</mat-option>
                          <mat-option value="On-site">On-site</mat-option>
                        </mat-select>
                      </mat-form-field>
                      <mat-form-field appearance="fill" subscriptSizing="dynamic">
                        <mat-label>Location</mat-label>
                        <input
                          matInput
                          [(ngModel)]="job.location"
                          [ngModelOptions]="{ standalone: true }"
                          aria-label="Job location"
                          (ngModelChange)="syncJobListings()"
                        />
                      </mat-form-field>
                      <mat-form-field appearance="fill" subscriptSizing="dynamic">
                        <mat-label>Salary (optional)</mat-label>
                        <input
                          matInput
                          [(ngModel)]="job.salary"
                          [ngModelOptions]="{ standalone: true }"
                          aria-label="Salary optional"
                          (ngModelChange)="syncJobListings()"
                        />
                      </mat-form-field>
                    </div>
                  </div>
                  <button
                    mat-icon-button
                    class="ce-remove-btn"
                    type="button"
                    [attr.aria-label]="'Remove ' + (job.title || 'this listing')"
                    (click)="removeJob(job.id)"
                    matTooltip="Remove"
                  >
                    <mat-icon aria-hidden="true">close</mat-icon>
                  </button>
                </div>
              }

              @if (jobListings.length === 0) {
                <div class="ce-empty-state" role="status">
                  No listings yet. Add sample or real job listings for students.
                </div>
              }
            </mat-card-content>
          </mat-card>
        </div>

        <!-- RIGHT: SIDEBAR -->
        <aside class="ce-sidebar" aria-label="Career page settings">
          <!-- RELATED CAREERS -->
          <mat-card class="ce-card" appearance="outlined">
            <mat-card-content>
              <h2 class="ce-section-title">Related careers</h2>
              <div class="ce-tag-input-row">
                <mat-form-field appearance="fill" class="ce-tag-field" subscriptSizing="dynamic">
                  <mat-label>Search careers</mat-label>
                  <input
                    matInput
                    [(ngModel)]="relatedInput"
                    [ngModelOptions]="{ standalone: true }"
                    [matAutocomplete]="relatedAuto"
                    aria-label="Search related careers"
                    (ngModelChange)="filterCareers(searchInput.value)"
                    #searchInput
                  />
                  <mat-autocomplete
                    #relatedAuto="matAutocomplete"
                    [displayWith]="displayCareer"
                    (optionSelected)="addRelated($event.option.value)"
                  >
                    @for (career of filteredCareers; track career.id) {
                      <mat-option [value]="career" [attr.aria-label]="career.name">
                        {{ career.icon }} {{ career.name }}
                      </mat-option>
                    }
                  </mat-autocomplete>
                </mat-form-field>
              </div>
              <div class="ce-related-list" role="list" aria-label="Related careers">
                @for (career of relatedCareers; track career.id) {
                  <div class="ce-related-item" role="listitem">
                    <span>{{ career.icon }} {{ career.name }}</span>
                    <button
                      mat-icon-button
                      class="ce-remove-btn-sm"
                      type="button"
                      [attr.aria-label]="'Remove ' + career.name + ' from related careers'"
                      (click)="removeRelated(career.id)"
                    >
                      <mat-icon aria-hidden="true">close</mat-icon>
                    </button>
                  </div>
                }
              </div>
            </mat-card-content>
          </mat-card>

          <!-- LINKED POSTS -->
          <mat-card class="ce-card" appearance="outlined">
            <mat-card-content>
              <h2 class="ce-section-title">Link counselor posts</h2>
              <p class="cd-subtitle" style="font-size: 12px; margin-bottom: 12px">
                Posts you link with this career will appear on the student page.
              </p>
              @for (post of availablePosts; track post.id; let i = $index) {
                <div class="ce-post-item">
                  <mat-checkbox
                    [(ngModel)]="availablePosts[i].linked"
                    [ngModelOptions]="{ standalone: true }"
                    color="primary"
                    class="ce-post-check"
                    [attr.aria-label]="'Link post: ' + stripHtml(post.content).slice(0, 60)"
                  >
                    <div>
                      <div class="ce-post-title">
                        {{ stripHtml(post.content) | slice: 0 : 80 }}...
                      </div>
                      <div class="ce-post-date">{{ post.date | date: 'mediumDate' }}</div>
                    </div>
                  </mat-checkbox>
                </div>
              }
              @if (availablePosts.length === 0) {
                <div class="ce-empty-state" role="status">
                  No posts yet. Create a post to link it here.
                </div>
              }
            </mat-card-content>
          </mat-card>

          <!-- VISIBILITY -->
          <mat-card class="ce-card" appearance="outlined">
            <mat-card-content>
              <h2 class="ce-section-title">Visibility</h2>
              <mat-radio-group
                formControlName="status"
                class="ce-radio-group"
                aria-label="Career page visibility"
              >
                <mat-radio-button value="Published" color="primary" class="ce-radio">
                  <div>
                    <div class="ce-radio-label">Published</div>
                    <div class="ce-radio-desc">Visible to your students</div>
                  </div>
                </mat-radio-button>
                <mat-radio-button value="Draft" color="primary" class="ce-radio">
                  <div>
                    <div class="ce-radio-label">Draft</div>
                    <div class="ce-radio-desc">Only visible to you</div>
                  </div>
                </mat-radio-button>
              </mat-radio-group>
            </mat-card-content>
          </mat-card>
        </aside>
      </div>
    </form>
  </main>
</div>
